import React, { useState, useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Modal,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import SalesOrderInvoice from 'components/Invoices/SalesOrderInvoice';
import PurchaseOrderInvoice from 'components/Invoices/PurchaseOrderInvoice';
import ReturnOrderInvoice from 'components/Invoices/ReturnOrderInvoice';

const InvoicesDisplay = (props) => {

  const printRef = useRef();

  const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
  const [switchy, setSwitchy] = useState(0);
  const [invoiceOption, setinvoiceOption] = useState({});

  const SOInvoiceOptions = useMemo(() => {
    const initialSOInvoices = [];

    for ( let i = 0; i < props.salesOrders.length; i++ ) {
      const salesOrder = props.salesOrders[i];
      initialSOInvoices.push({
        salesOrderId: salesOrder.id,
        displayId: salesOrder.displayId
      });
    }

    return initialSOInvoices;

  }, [props.salesOrders]);
  
  const POInvoiceOptions = useMemo(() => {
    const initialPOInvoices = [];

    for ( let i = 0; i < props.purchaseOrders.length; i++ ) {
      const purchaseOrder = props.purchaseOrders[i];
      for ( let j = 0; j < purchaseOrder.payments.length; j++ ) {
        initialPOInvoices.push({
          purchaseOrderId: purchaseOrder.id,
          displayId: purchaseOrder.payments[j].displayId
        });
      }
    }

    return initialPOInvoices;

  }, [props.purchaseOrders]);
    
  const ROInvoiceOptions = useMemo(() => {
    const initialROInvoices = [];

    for ( let i = 0; i < props.returnOrders.length; i++ ) {
      const returnOrder = props.returnOrders[i];
      initialROInvoices.push({
        returnOrderId: returnOrder.id,
        displayId: returnOrder.displayId
      });
    }

    return initialROInvoices;

  }, [props.returnOrders]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Return Order Invoice"
  });

  const renderInvoice = () => {
    
    switch (switchy) {

      //for Sales Orders.
      case 0:
        
        const salesOrderInfo = props.salesOrders.find(salesOrder => salesOrder.id === invoiceOption.salesOrderId);
        const customerInfo = props.customers.find(customer => customer.id === salesOrderInfo.customerId);
        const soProducts = salesOrderInfo.products.map(salesOrderProduct => {
          const productInfo = props.products.find(product => product.id === salesOrderProduct.productId);
          return {
            displayId: productInfo.displayId,
            name: productInfo.name,
            company: productInfo.company,
            ...salesOrderProduct
          }
        });
        const SOInvoiceInfo = {
          displayId: salesOrderInfo.displayId,
          customerName: customerInfo ? customerInfo.name : "",
          customerPhone: customerInfo ? customerInfo.phone : "",
          paymentDate: salesOrderInfo.createdAt,
          subTotal: salesOrderInfo.total - salesOrderInfo.totalVat,
          taxTotal: salesOrderInfo.totalVat,
          grandTotal: salesOrderInfo.total,
          paymentMethod: salesOrderInfo.paymentMethod,
          products: soProducts
        };

        return (
          <SalesOrderInvoice
            ref={printRef}
            info={SOInvoiceInfo}
            user={props.user}
          />
        )
      
      //for PurchaseOrders.
      case 1:

        const purchaseOrderInfo = props.purchaseOrders.find(purchaseOrder => purchaseOrder.id === invoiceOption.purchaseOrderId);
        const poPaymentInfo = purchaseOrderInfo.payments.find(payment => payment.displayId === invoiceOption.displayId);
        const poVendorInfo = props.vendors.find(vendor => vendor.id === purchaseOrderInfo.vendorId);
        const poProducts = purchaseOrderInfo.products.map(purchaseOrderProduct => {
          const productInfo = props.products.find(product => product.id === purchaseOrderProduct.productId);
          return {
            displayId: productInfo.displayId,
            name: productInfo.name,
            company: productInfo.company,
            unitCost: productInfo.unitCost,
            ...purchaseOrderProduct
          }
        });
        const POInvoiceInfo = {
          displayId: poPaymentInfo.displayId,
          vendorName: poVendorInfo.name,
          vendorTrn: poVendorInfo.trn,
          paymentDate: poPaymentInfo.createdAt,
          subTotal: poPaymentInfo.amount - poPaymentInfo.amountVat,
          taxTotal: poPaymentInfo.amountVat,
          currentAmount: poPaymentInfo.amount,
          grandTotal: purchaseOrderInfo.total,
          products: poProducts,
          payments: purchaseOrderInfo.payments,
          status: purchaseOrderInfo.status
        };

        return (
          <PurchaseOrderInvoice
            ref={printRef}
            info={POInvoiceInfo}
            user={props.user}
          />
        )
      
      //for ReturnOrders.
      case 2:

        const returnOrderInfo = props.returnOrders.find(returnOrder => returnOrder.id === invoiceOption.returnOrderId);
        const roVendorInfo = props.vendors.find(vendor => vendor.id === returnOrderInfo.vendorId);
        const roProducts = returnOrderInfo.products.map(returnOrderProduct => {
          const productInfo = props.products.find(product => product.id === returnOrderProduct.productId);
          return {
            displayId: productInfo.displayId,
            name: productInfo.name,
            company: productInfo.company,
            unitCost: productInfo.unitCost,
            ...returnOrderProduct
          }
        });
        const ROInvoiceInfo = {
          displayId: returnOrderInfo.displayId,
          vendorName: roVendorInfo.name,
          vendorTrn: roVendorInfo.trn,
          paymentDate: returnOrderInfo.createdAt,
          subTotal: returnOrderInfo.total - returnOrderInfo.totalVat,
          taxTotal: returnOrderInfo.totalVat,
          grandTotal: returnOrderInfo.total,
          paymentByCash: returnOrderInfo.paymentByCash,
          products: roProducts
        };

        return (
          <ReturnOrderInvoice
            ref={printRef}
            info={ROInvoiceInfo}
            user={props.user}
          />
        )
            
      default:

        return (<React.Fragment></React.Fragment>)
    }
  };

  const handleInvoiceModalOpen = (option, switchy) => {
    setinvoiceOption(option);
    setSwitchy(switchy);
    setOpenInvoiceModal(true);
  };

  const handleInvoiceModalClose = () => {
    setinvoiceOption({});
    setSwitchy(0);
    setOpenInvoiceModal(false);
  };
  
  return (
    <React.Fragment>
      <Typography
        color="textPrimary"
        variant="h4"
      >
        Invoice Printing
      </Typography>
      <Grid
        container
        spacing={3}
      >
        <Grid
          item
          md={3}
          xs={12}
        >
          <p>Print Sales Order Invoice</p>
          <Autocomplete
            onChange={(event, newValue) => {
              if(newValue) handleInvoiceModalOpen(newValue, 0);
            }}
            id="free-solo-dialog-demo"
            options={SOInvoiceOptions}
            getOptionSelected={(option, value) => option.displayId === value.displayId}
            getOptionLabel={(option) => option.displayId}
            style={{ width: 300 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Sales Order ID.."
                variant="outlined"
                helperText="Please select the Sales Order Invoice to be printed"
              />
            )}
            value={null}
          />
        </Grid>
        <Grid
          item
          md={3}
          xs={12}
        >
          <p>Print Purchase Order Invoice</p>
          <Autocomplete
            onChange={(event, newValue) => {
              if(newValue) handleInvoiceModalOpen(newValue, 1);
            }}
            id="free-solo-dialog-demo"
            options={POInvoiceOptions}
            getOptionSelected={(option, value) => option.displayId === value.displayId}
            getOptionLabel={(option) => option.displayId}
            style={{ width: 300 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Purchase Order Invoice.."
                variant="outlined"
                helperText="Please select the Purchase Order Invoice to be printed"
              />
            )}
            value={null}
          />
        </Grid>
        <Grid
          item
          md={3}
          xs={12}
        >
          <p>Print Return Order Invoice</p>
          <Autocomplete
            onChange={(event, newValue) => {
              if(newValue) handleInvoiceModalOpen(newValue, 2);
            }}
            id="free-solo-dialog-demo"
            options={ROInvoiceOptions}
            getOptionSelected={(option, value) => option.displayId === value.displayId}
            getOptionLabel={(option) => option.displayId}
            style={{ width: 300 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Return Order ID.."
                variant="outlined"
                helperText="Please select the Return Order Invoice to be printed"
              />
            )}
            value={null}
          />
        </Grid>
      </Grid>
      { openInvoiceModal ?
        <Modal open={openInvoiceModal} >
          <Card>
            <CardHeader
              subheader="Please Review The Invoice"
              title="Print Invoice"
            />
            <Divider />
            <CardContent>
              <Box>
                <Paper
                  style={{
                    overflow: 'auto',
                    maxHeight: 469
                  }}
                >
                  {renderInvoice()}
                </Paper>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '1%'
                  }}
                >
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handlePrint}
                  >
                    Print Invoice
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleInvoiceModalClose}
                  >
                    Close
                  </Button>
                </Box>
              </Box>
            </CardContent>
            <Divider />
          </Card>
        </Modal>
      : null }
    </React.Fragment>
  )
};

export default InvoicesDisplay;